import { Box, Heading, HStack, Text, Tooltip, VStack } from "@reverb-ui/react";
import { VisitorsFunnel as VisitorsFunnelInterface } from "components/Highlights/interface/highlights";
import { numberWithCommas } from "helper/cardsHelper";
import { useMemo } from "react";
import { HeaderCard } from "./HeaderCard";

export interface VisitorsFunnelProps {
  visitors?: VisitorsFunnelInterface[];
}

const getBarColorByType = (type: string) => {
  const barColorMap: Record<string, string> = {
    "UNIQUE VISITORS": "primary.200",
    ENGAGED: "primary.400",
    CONVERTED: "primary.500",
  };

  return barColorMap[type] || "blue.500";
};

const getBarHeight = (quantity: number, totalVisitors: number): string => {
  return `calc(${Math.log((quantity / totalVisitors) * 100) * 9}%)`;
};

type TVisitorFunnelBar = {
  quantity: number;
  percentage: string | number;
  color: string;
  height?: string;
  type: string;
  tooltipMessage?: string;
};

enum EVisitorsFunnel {
  VISITORS = "UNIQUE VISITORS",
  ENGAGED = "ENGAGED",
  CONVERSIONS = "CONVERTED",
}

const funnelInfo = {
  funnel: [
    {
      id: EVisitorsFunnel.ENGAGED,
      tooltipMessage:
        "The total number of unique visitors who have clicked on a tagged action on the experience.",
    },
  ],
};

const VisitorsFunnel = ({
  visitors = [],
}: VisitorsFunnelProps): JSX.Element => {
  const totalVisitors = useMemo(
    () => visitors?.reduce((acc, visitor) => visitor.quantity + acc, 0),
    [visitors]
  );

  const visitorFunnelBars = useMemo<TVisitorFunnelBar[]>(
    () =>
      visitors.map((visitor) => ({
        color: getBarColorByType(visitor.type),
        quantity: visitor.quantity,
        type: visitor.type,
        percentage: visitor.percentage,
        height: getBarHeight(visitor.quantity, totalVisitors || 2),
        tooltipMessage: funnelInfo.funnel.find((_) => _.id === visitor.type)
          ?.tooltipMessage,
      })),
    [totalVisitors, visitors]
  );

  return (
    <VStack
      bgColor="white"
      h="full"
      spacing={4}
      alignItems="start"
      p={6}
      borderRadius="0.6rem"
    >
      <HeaderCard title="Visitors funnel" iconName="Funnel" />
      <HStack w="full" flex={1} spacing={4}>
        <VStack h="full" flex={1} alignItems="flex-end">
          {visitorFunnelBars.map((visitor, index) => (
            <Box
              key={index}
              bg={visitor.color}
              width={`${100 - index * 25}%`}
              borderRadius="base"
              height={visitor.height}
              minH={3}
            />
          ))}
        </VStack>
        <VStack flex={1} h="full" alignItems="flex-start">
          {visitorFunnelBars.map((visitor, index) => (
            <Tooltip
              key={index}
              bg="gray.100"
              color="gray.800"
              label={visitor?.tooltipMessage}
              isDisabled={!visitor?.tooltipMessage}
            >
              <Box
                h={visitor.height}
                minH={10}
                key={index}
                display="grid"
                alignContent="end"
              >
                <HStack pb={3}>
                  <Heading fontSize="md" whiteSpace="nowrap">
                    {visitor.percentage}
                  </Heading>
                  <Text fontSize="xs">
                    {visitor.type} ({numberWithCommas(visitor.quantity)})
                  </Text>
                </HStack>
                <Box borderRadius={4} w="55px" h="1" bg={visitor.color} />
              </Box>
            </Tooltip>
          ))}
        </VStack>
      </HStack>
    </VStack>
  );
};

export default VisitorsFunnel;
